import * as THREE from 'three';
import { PanoControls } from '../controls';
import { GeocentricCoordinate, sphereRaycast } from '../projections';
import { setPointScale } from '../utilities';
import { MeasurementArea, MeasurementVolume } from './measurement';
import MeasurementController from './measurement-controller';

export class MeasureEndpoint extends THREE.Mesh {
  public measurementVertex;
  public measurementID;

  constructor(geometry, material, index, id) {
    super(geometry, material);
    this.measurementVertex = index;
    this.measurementID = id;
  }
}

export class TemporaryEndpoint extends THREE.Mesh {
  constructor(geometry, material) {
    super(geometry, material);
  }
}

export default class MeasurementSceneView {
  private controller: MeasurementController;
  private scene: THREE.Scene;

  private sphereBuffer = 1.2;
  private sphereRadius = 0.05;
  private sphereGeometry = new THREE.SphereGeometry(this.sphereRadius, 20, 20);
  private sphereMaterialStandard = new THREE.MeshBasicMaterial({color: 0xffffff});
  private sphereMaterialHover = new THREE.MeshBasicMaterial({color: 0xffcc33});
  private lineMaterialStandard = new THREE.LineBasicMaterial({color: 0x008B8B});
  private lineMaterialHover = new THREE.LineBasicMaterial({color: 0xffcc33});

  private measurements = [];
  private temporaryEndpoints = [];
  private temporaryLines = [];
  public hoveredPoint = null;
  public draggable = false;
  private dragObject:MeasureEndpoint = null;
  private dragValid = false;

  constructor(controller, scene) {
    this.controller = controller;
    this.scene = scene;
  }

  get viewer() {
    return this.controller.viewer;
  }

  updateMeasureEndpoint(coordinate) {
    if (!this.draggable) {
      return;
    }

    if (!coordinate) {
      return;
    }

    let id = this.dragObject.measurementID;
    let vertexIndex = this.dragObject.measurementVertex;
    let measurement = this.controller.measurementGroup.measurements.find(
      measurement => measurement.id === id
    );

    if (!measurement) {
      return;
    }

    if (measurement instanceof MeasurementVolume) {
      measurement.stopVolumeCalculation();
    }

    this.dragValid = true;

    // Update our measurement vertex value
    let vertex = new GeocentricCoordinate().fromScene(coordinate);
    measurement.vertices[vertexIndex] = vertex;

    // Remove + redraw the line
    this.removeByID(id);
    this.draw(measurement);
  }

  measureGrabFinished() {
    if ((!this.draggable) || (!this.dragValid)) {
      return;
    }

    this.controller.refreshMeasurementList();
  }

  checkEndpointSelected(event, controls: PanoControls) {
    let controller = this.controller;
    if (!controller.editable) {
      return;
    }

    if (controller.measurementBuilder.activeMeasurement) {
      return;
    }

    let targets = this.scene.children
      .filter(x => x instanceof MeasureEndpoint);

    let objects = controls.getRaycastResults(event, {
      measureData: true,
      returnObject: true,
      sortByRay: true,
      targets: targets
    });

    let draggable = objects.length > 0;

    if (draggable) {
      this.setDragState(true, objects[0].object);
    } else {
      this.setDragState(false, null);
    }
  }

  setDragState(grabbing, dragObject) {
    this.draggable = grabbing;
    this.dragObject = dragObject;
  }

  hideTooltip() {
    this.controller.setViewerTooltipText("");
  }

  setPointGrabTooltip() {
    this.controller.setViewerTooltipText("Click and hold to move vertex");
  }

  setRestrictedPointTooltip() {
    this.controller.setViewerTooltipText("Unable to move this measurement type");
  }

  setTooltipText(measurement, morepoints = false) {
    let text = "";

    if (morepoints) {
      let endTooltipCount = measurement.minVertexRequired - 1;
      endTooltipCount = Math.max(2, endTooltipCount);

      if (measurement.vertices.length < endTooltipCount) {
        text = measurement.moreTooltip;
      } else {
        text = measurement.endTooltip;
      }
    } else {
      text = measurement.startTooltip;
    }

    this.controller.setViewerTooltipText(text);
  }

  setPointScale(mesh) {
    setPointScale(this.viewer, mesh, this.sphereBuffer);
  }

  addTemporaryPoint(coordinate) {
    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.temporaryEndpoints.push(sphere);
  }

  addActiveEndpoint(coordinate) {
    if (coordinate === null) {
      return;
    }

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    let sphere = this.createTempEndpoint(coordinate);
    this.scene.add(sphere);
    this.hoveredPoint = sphere;

    // Remove all previous lines
    for (let temporaryLine of this.temporaryLines) {
      this.scene.remove(temporaryLine);
    }
    this.temporaryLines = [];

    // Re-draw the entire temporary line
    let points = [...this.temporaryEndpoints, this.hoveredPoint];
    for (var i=1;i<points.length;i++) {
      let line = this.createLine(points[i].position, points[i-1].position);
      this.scene.add(line);
      this.temporaryLines.push(line);
    }
  }

  createEndpoint(coordinates, index, id) {
    let sphere = new MeasureEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard, index, id);

    sphere.position.copy(coordinates);
    this.setPointScale(sphere);

    sphere.geometry.computeBoundingSphere();
    sphere.geometry.boundingSphere.radius *= this.sphereBuffer;
    sphere.raycast = sphereRaycast;

    return sphere;
  }

  createTempEndpoint(coordinate) {
    let sphere = new TemporaryEndpoint(this.sphereGeometry,
      this.sphereMaterialStandard);

    sphere.position.copy(coordinate);
    this.setPointScale(sphere);

    return sphere;
  }

  createLine(start, end) {
    let lineGeometry = new THREE.Geometry();

    lineGeometry.vertices.push(start.clone());
    lineGeometry.vertices.push(end.clone());

    return new THREE.Line(lineGeometry, this.lineMaterialStandard);
  }

  updateMaterial(id, hover) {
    let material;

    let measurement = this.measurements.find(measurement => measurement.id === id);
    if (measurement) {
      for (let object of measurement.lines) {
        material = hover ? this.lineMaterialHover : this.lineMaterialStandard;
        object.material = material;
        object.material.needsUpdate = true;
      }
      for (let object of measurement.points) {
        material = hover ? this.sphereMaterialHover : this.sphereMaterialStandard;
        object.material = material;
        object.material.needsUpdate = true;
      }
    }
  }

  draw(measurement) {
    let lines = [];
    let points = [];
    let point;
    let line;
    let vertices = measurement.vertices;

    for (var i = 0; i < vertices.length; i++) {
      let vertexA = measurement.vertices[i].toScene();
      point = this.createEndpoint(vertexA, i, measurement.id);
      this.scene.add(point);
      points.push(point);

      if (i >= 1) {
        let vertexB = measurement.vertices[i - 1].toScene();
        line = this.createLine(vertexA, vertexB);
        this.scene.add(line);
        lines.push(line);
      }
    }

    // Extra steps for area (close polygon + calculate surface)
    if (measurement instanceof MeasurementArea) {
      let vertexA = measurement.vertices[vertices.length - 1].toScene();
      let vertexB = measurement.vertices[0].toScene();
      line = this.createLine(vertexA, vertexB);
      this.scene.add(line);
      lines.push(line);

      measurement.generateSurface();
    }

    this.measurements.push({
      id: measurement.id,
      lines: lines,
      points: points
    });

    // Update area measurement shader
    this.controller.updateAreaShaders();
  }

  removeTemporaryObjects() {
    for (let temporaryEndpoint of this.temporaryEndpoints) {
      this.scene.remove(temporaryEndpoint);
    }

    for (let temporaryLine of this.temporaryLines) {
      this.scene.remove(temporaryLine);
    }

    if (this.hoveredPoint) {
      this.scene.remove(this.hoveredPoint);
    }

    this.temporaryEndpoints = [];
    this.temporaryLines = [];
    this.hoveredPoint = null;

  }

  removeByID(id) {
    let measurement = this.measurements.find(measurement => measurement.id === id);
    if (measurement) {
      for (let object of measurement.lines) {
        this.scene.remove(object);
      }
      for (let object of measurement.points) {
        this.scene.remove(object);
      }
      this.measurements = this.measurements.filter(measurement => measurement.id !== id);
    }

    let measurementGroup = this.controller.measurementGroup;
    let measurementObject = measurementGroup.measurements.find(
      measurement => measurement.id === id
    );

    measurementObject.clear();
  }

  removeAll() {
    for (let measurement of this.measurements) {
      for (let object of measurement.lines) {
        this.scene.remove(object);
      }
      for (let object of measurement.points) {
        this.scene.remove(object);
      }
    }
    this.measurements = [];
  }

  update(changeDetector) {
    if (changeDetector === null) {
      return;
    }

    const update = changeDetector.lookatChanged
      || changeDetector.fovChanged
      || changeDetector.zoomChanged
      || changeDetector.cameraChanged
      || changeDetector.heightChanged;

    if (!update) {
      return;
    }

    this.temporaryEndpoints.forEach(mesh => {
      this.setPointScale(mesh);
    });

    this.measurements.forEach(measurement => {
      measurement.points.forEach(mesh => {
        this.setPointScale(mesh);
      });
    });
  }
}
